import b from 'b_';
import get from 'lodash/get';
import { Table, TableBody, ThreeDots } from 'platform-components';
import { NOTIFICATION_THEMES } from 'platform-components/constants';
import { showIntlMessageNotification } from 'platform-components/src/components/notification/actions';
import { withRouter } from 'platform-components/src/components/router/router';
import { getExactPathMatch } from 'platform-components/src/utils/getExactPathMatch';
import { withRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, compose } from 'redux';

import * as studentsActions from 'common.components/students/actions';
import { getIsAddingNewStudent } from 'common.components/students/selectors';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import * as userActions from 'common.components/user/actions';
import { getClasses, getSchoolId } from 'common.components/user/selectors';
import { YM_LOCATION } from 'utils/ym-constants';

import ClassAddButton from '../__add-button/class__add-button';
import { ArchivateClassModal } from '../__archivate-class-modal/class__archivate-class-modal';
import { ClassRow } from '../__class-row/class__class-row';
import DeleteClassModal from '../__delete-class-modal/class__delete-class-modal';
import { ClassNameEditorModal } from '../__name-editor-modal/class__name-editor-modal';
import * as classActions from '../actions';
import {
    getClassDeleteValidationMessageId,
    getIsArchivateClassModalOpened,
    getIsClassNameEditorModalOpened,
} from '../selectors';

import './class__classes.scss';

const NOTIFICATION_CLOSE_DELAY = 3000;

export class Classes extends React.Component {
    static propTypes = {
        toggleArchivateClassModal: PropTypes.func.isRequired,
        cancelAddingNewStudent: PropTypes.func.isRequired,
        classDeleteValidationMessageId: PropTypes.string,
        classes: PropTypes.array.isRequired,
        currentTeamId: PropTypes.number,
        deleteClassRequest: PropTypes.func.isRequired,
        finishClassCreation: PropTypes.func.isRequired,
        getRouterPath: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired,
        isAddingNewStudent: PropTypes.bool,
        isArchivateClassModalOpened: PropTypes.bool,
        isClassNameEditorModalOpened: PropTypes.bool,
        match: PropTypes.object.isRequired,
        setClassItemToArchive: PropTypes.func.isRequired,
        showIntlMessageNotification: PropTypes.func.isRequired,
        startTeamBuilder: PropTypes.func.isRequired,
        toggleClassNameEditorModal: PropTypes.func.isRequired,
        schoolId: PropTypes.number,
        location: PropTypes.object.isRequired,
    };

    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    state = {
        classIdToDelete: null,
        showDeleteConfirmation: false,
    };

    handleDeleteClass = ({ id }) => {
        const { classDeleteValidationMessageId, currentTeamId } = this.props;

        ym('reachGoal', 'teacher_my_students_class_delete_press');

        if (currentTeamId === id && classDeleteValidationMessageId) {
            this.props.showIntlMessageNotification({
                messageId: classDeleteValidationMessageId,
                theme: NOTIFICATION_THEMES.FAIL,
                closeDelay: NOTIFICATION_CLOSE_DELAY,
            });
            return;
        }

        this.setState({ classIdToDelete: id, showDeleteConfirmation: true });
    };

    handleConfirmDeleting = () => {
        ym('reachGoal', 'teacher_my_students_class_delete_success');

        const {
            classes,
            history,
            getRouterPath,
            deleteClassRequest,
            match: {
                params: { classId },
            },
        } = this.props;
        const { classIdToDelete } = this.state;
        const {
            staticUrls: { labClassStudents },
        } = this.context;
        const isLastClass = classes.length === 1;
        const isDeletingCurrentClass = Number(classId) === Number(classIdToDelete);
        const classItem = classes.find((item) => item.id === classIdToDelete);

        deleteClassRequest(classItem);

        this.setState({ showDeleteConfirmation: false });

        if (isDeletingCurrentClass && !isLastClass) {
            const existingClassId = classes.find((item) => item.id !== classIdToDelete).id;

            history.push(getRouterPath(labClassStudents, { classId: existingClassId }));
        }
    };

    handleRejectDeleting = () => {
        ym('reachGoal', 'teacher_my_students_class_delete_fail');

        this.setState({ showDeleteConfirmation: false });
    };

    handleEditClass = (classItem) => {
        const { getRouterPath, history, toggleClassNameEditorModal } = this.props;
        const {
            staticUrls: { labClassStudents },
        } = this.context;

        history.push(getRouterPath(labClassStudents, { classId: classItem.id }));

        ym('reachGoal', 'teacher_my_students_class_rename');

        toggleClassNameEditorModal(true);
    };

    handleArchiveClass = (classItem) => {
        const { cancelAddingNewStudent, isAddingNewStudent, setClassItemToArchive, toggleArchivateClassModal } =
            this.props;

        setClassItemToArchive(classItem);
        toggleArchivateClassModal(true);

        if (isAddingNewStudent) {
            cancelAddingNewStudent();
        }
    };

    handleConfirmArchivating = () => {
        const { toggleArchivateClassModal } = this.props;

        toggleArchivateClassModal(false);
    };

    handleRejectArchivating = () => {
        const { toggleArchivateClassModal } = this.props;

        toggleArchivateClassModal(false);
    };

    closeClassNameModalEditor = () => {
        const { toggleClassNameEditorModal } = this.props;

        toggleClassNameEditorModal(false);
    };

    addClass = () => {
        const { startTeamBuilder, location } = this.props;
        const { staticUrls } = this.context;
        const { path = '' } = getExactPathMatch(staticUrls, location.pathname) || {};

        ym('reachGoal', 'click_add_class', { from: path });

        startTeamBuilder({
            entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
            location: YM_LOCATION.CLASS,
            onSuccess: this.handleFinishTeamCreation,
        });
    };

    handleFinishTeamCreation = (addedStudentsIds, createdTeamId) => {
        const { finishClassCreation } = this.props;

        finishClassCreation(createdTeamId);
    };

    getMenuOptions = () => [
        {
            onClick: this.handleEditClass,
            textId: 'class.edit',
        },
        {
            onClick: this.handleArchiveClass,
            textId: 'class.archive',
        },
        {
            onClick: this.handleDeleteClass,
            textId: 'class.delete',
            type: ThreeDots.TYPES.STRICT_WARNED,
            withBorder: true,
        },
    ];

    render() {
        const { classes, currentTeamId, isArchivateClassModalOpened, isClassNameEditorModalOpened } = this.props;
        const { classIdToDelete, showDeleteConfirmation } = this.state;
        const classToDelete = classes.find((classItem) => classItem.id === classIdToDelete);
        const currentClass = classes.find((classItem) => classItem.id === currentTeamId);

        return (
            <Table mix="class__classes qa_active-teams">
                <TableBody mix="class__classes-body">
                    {classes.map((classItem) => (
                        <ClassRow key={classItem.id} menuOptions={this.getMenuOptions()} value={classItem} />
                    ))}
                    <ClassAddButton
                        mix={classes.length === 0 ? b('class', 'class-row', { selected: true }) : ''}
                        onClick={this.addClass}
                        textId="class.button.addClass"
                    />
                </TableBody>
                <ClassNameEditorModal
                    onClose={this.closeClassNameModalEditor}
                    opened={isClassNameEditorModalOpened}
                    teamGrade={currentClass?.level}
                    teamId={currentTeamId}
                />
                <DeleteClassModal
                    classId={currentTeamId}
                    level={get(classToDelete, 'level')}
                    name={get(classToDelete, 'name')}
                    onClose={this.handleRejectDeleting}
                    onConfirm={this.handleConfirmDeleting}
                    onReject={this.handleRejectDeleting}
                    opened={showDeleteConfirmation}
                />
                <ArchivateClassModal
                    onConfirm={this.handleConfirmArchivating}
                    onReject={this.handleRejectArchivating}
                    opened={isArchivateClassModalOpened}
                />
            </Table>
        );
    }
}

const mapStateToProps = (storeState, ownProps) => {
    const {
        match: {
            params: { classId },
        },
    } = ownProps;

    return {
        classes: getClasses(storeState),
        classDeleteValidationMessageId: getClassDeleteValidationMessageId(storeState, { classId }),
        currentTeamId: Number(classId),
        isAddingNewStudent: getIsAddingNewStudent(storeState),
        isClassNameEditorModalOpened: getIsClassNameEditorModalOpened(storeState),
        isArchivateClassModalOpened: getIsArchivateClassModalOpened(storeState),
        schoolId: getSchoolId(storeState),
    };
};

const mapDispatchToProps = (dispatch) => {
    const { toggleArchivateClassModal, toggleClassNameEditorModal } = classActions;
    const { finishClassCreation, setClassItemToArchive } = classActions;
    const { cancelAddingNewStudent } = studentsActions;
    const { deleteClassRequest } = userActions;
    const { start: startTeamBuilder } = teamBuilderActions;

    return bindActionCreators(
        {
            cancelAddingNewStudent,
            deleteClassRequest,
            finishClassCreation,
            toggleArchivateClassModal,
            toggleClassNameEditorModal,
            setClassItemToArchive,
            showIntlMessageNotification,
            startTeamBuilder,
        },
        dispatch
    );
};

export default compose(withRouter, withRouterPath, connect(mapStateToProps, mapDispatchToProps))(Classes);
